import React, { useMemo } from 'react';
import AdditionalSettings from '../../../../../components/Builder/AdditionalSettings';
import BuilderSamplePercentage from '../../../../../components/Builder/BuilderFields/BuilderSamplePercentage';
import { useAppSelector } from '../../../../../redux/hooks';
import {
  ExpressionModeKey,
  ExpressionQuerySettingKey,
  IExpressionQuerySetting,
  PermissionKey,
  QueryType,
} from '../../../../../utils/apollo/graphqlSchema';
import BuilderExpressionMode from './BuilderExpressionMode';
import {
  activeSchemaIdSelector,
  additionalSettingsSelector,
  builderDefaultOptionsSelector,
  builderSelectedOptionsSelector,
} from '../../../../../redux/selectors/builder';
import { assertCondition } from '../../../../../utils/types';
import { expressionAdditionalSettingsTooltips } from '../../../../../components/KubitTooltip/globalTooltips';
import { BuilderHeader } from '../../../../../components/Builder/BuilderHeader';
import ThinDivider from '../../../../../components/ThinDivider';
import { UnreachableCaseError } from 'ts-essentials';
import LumosButton from '../../../../../components/LumosButton';
import useShowLumosButton from '../../../../../components/LumosButton/useShowLumosButton';

const QueryBuilderHeader = () => {
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const additionalSettings = useAppSelector(additionalSettingsSelector);
  const defaultOptions = useAppSelector(builderDefaultOptionsSelector);
  const selectedOptions = useAppSelector(builderSelectedOptionsSelector);

  assertCondition(
    defaultOptions?.type === QueryType.Expression &&
      selectedOptions?.type === QueryType.Expression,
  );

  const shouldShowLumosButton = useShowLumosButton(
    activeSchemaId,
    PermissionKey.AnalysisCreate,
  );

  const { queryModeConfig, impactModeConfig, histogramModeConfig } =
    defaultOptions;
  const { mode } = selectedOptions;

  const additionalSettingsOptions = useMemo(() => {
    let settings: IExpressionQuerySetting[] = [];

    // Settings array values are based on selected Query mode
    switch (mode) {
      case ExpressionModeKey.Query:
        settings = queryModeConfig.settings;
        break;
      case ExpressionModeKey.Impact:
        settings = impactModeConfig.settings;
        break;
      case ExpressionModeKey.Histogram:
        settings = histogramModeConfig.settings;
        break;
      default:
        throw new UnreachableCaseError(mode);
    }

    return settings.map((setting) => ({
      ...setting,
      tooltip: expressionAdditionalSettingsTooltips[setting.key],
    }));
  }, [histogramModeConfig, impactModeConfig, mode, queryModeConfig]);

  const checkSelectedSettings = (key: ExpressionQuerySettingKey) =>
    additionalSettings.includes(key);

  return (
    <BuilderHeader queryType={QueryType.Expression}>
      <ThinDivider layout="vertical" size="short" />
      <BuilderExpressionMode />
      {checkSelectedSettings(ExpressionQuerySettingKey.Sampling) && (
        <>
          <ThinDivider layout="vertical" size="short" />
          <BuilderSamplePercentage />
        </>
      )}

      {additionalSettingsOptions.length > 0 && (
        <>
          <ThinDivider layout="vertical" size="short" />
          <AdditionalSettings
            options={additionalSettingsOptions}
            selectedKeys={additionalSettings}
          />
        </>
      )}
      {shouldShowLumosButton && <LumosButton />}
    </BuilderHeader>
  );
};

export default QueryBuilderHeader;
